import axios from 'axios';
import { isDebugMode } from '../lib/debug';
import { getApiBaseUrl } from '../lib/config';

const api = axios.create({
    // baseURL will be set dynamically in the request interceptor
    timeout: 30000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: false, // Don't send cookies (CSRF tokens are in cookies)
});

// Request interceptor to set baseURL dynamically and add auth token
api.interceptors.request.use(
    (config) => {
        // Always set baseURL dynamically on each request to ensure runtime config is read
        // This ensures window.__APP_CONFIG__ is available even if set after module load
        config.baseURL = getApiBaseUrl();
        config.headers['X-Client-Platform'] = 'web';
        
        // When sending FormData, let the browser set Content-Type with boundary (required for multipart)
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Log error details only in debug mode
        if (isDebugMode && error.response) {
            console.error('API Error:', {
                status: error.response.status,
                data: error.response.data,
                headers: error.response.headers,
            });
        }

        if (error.response?.status === 401) {
            // Unauthorized - token expired or invalid
            const adminToken = localStorage.getItem('admin_auth_token');
            const adminUser = localStorage.getItem('admin_user');
            if (adminToken) {
                localStorage.setItem('auth_token', adminToken);
                if (adminUser) {
                    localStorage.setItem('user', adminUser);
                }
                localStorage.removeItem('admin_auth_token');
                localStorage.removeItem('admin_user');
                window.location.href = '/pilot/dashboard';
                return Promise.reject(error);
            }

            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            
            // Determine redirect path based on current location
            const currentPath = window.location.pathname;
            if (currentPath.startsWith('/pilot')) {
                window.location.href = '/pilot/login';
            } else {
                window.location.href = '/login';
            }
        }

        // Handle CSRF token mismatch specifically
        if (isDebugMode && (error.response?.status === 419 || error.response?.data?.message?.includes('CSRF'))) {
            console.error('CSRF token mismatch. This might be a CORS or API configuration issue.');
            // For API routes, CSRF shouldn't be required, but if it is, we'd need to fetch a token first
        }

        return Promise.reject(error);
    }
);

export default api;

